import { Users, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";

interface Step4Props {
  data: {
    preferredTeamSize: string;
    culturePrefs: {
      collaborative: number;
      structured: number;
      traditional: number;
    };
  };
  onUpdate: (data: any) => void;
  onNext: () => void;
  onBack: () => void;
}

const teamSizes = [
  { id: "solo", label: "Solo", description: "Just me" },
  { id: "small", label: "Small", description: "2-10 agents" },
  { id: "medium", label: "Medium", description: "11-25 agents" },
  { id: "large", label: "Large", description: "26+ agents" },
];

const OnboardingStep4 = ({ data, onUpdate, onNext, onBack }: Step4Props) => {
  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-bold text-foreground">Help us find your perfect match</h2>
        <p className="text-muted-foreground">Fine-tune your preferences</p>
      </div>

      <div className="space-y-6">
        {/* Preferred Team Size */}
        <div className="space-y-3">
          <Label className="flex items-center gap-2">
            <Users className="h-4 w-4 text-primary" />
            Preferred team size
          </Label>
          <div className="grid grid-cols-4 gap-2">
            {teamSizes.map((size) => {
              const isSelected = data.preferredTeamSize === size.id;
              return (
                <button
                  key={size.id}
                  onClick={() => onUpdate({ ...data, preferredTeamSize: size.id })}
                  className={`p-3 rounded-xl border text-center transition-all ${
                    isSelected
                      ? "border-primary bg-primary/10"
                      : "border-border hover:bg-muted"
                  }`}
                >
                  <p className={`text-sm font-semibold ${isSelected ? "text-primary" : "text-foreground"}`}>
                    {size.label}
                  </p>
                  <p className="text-xs text-muted-foreground">{size.description}</p>
                </button>
              );
            })}
          </div>
        </div>

        {/* Culture Fit Sliders */}
        <div className="space-y-4">
          <Label>Culture Fit</Label>
          <div className="space-y-6 bg-gradient-to-br from-primary/5 to-transparent rounded-xl p-4 border border-primary/10">
            {/* Collaborative vs Independent */}
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="font-medium text-foreground">Collaborative</span>
                <span className="font-medium text-foreground">Independent</span>
              </div>
              <div className="relative">
                <Slider
                  value={[data.culturePrefs.collaborative]}
                  onValueChange={(value) => 
                    onUpdate({ 
                      ...data, 
                      culturePrefs: { ...data.culturePrefs, collaborative: value[0] } 
                    })
                  }
                  min={0}
                  max={100}
                  step={10}
                  className="py-2"
                />
                <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 flex justify-center pointer-events-none">
                  <div className="w-px h-4 bg-border" />
                </div>
              </div>
            </div>

            {/* Structured vs Flexible */}
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="font-medium text-foreground">Structured</span>
                <span className="font-medium text-foreground">Flexible</span>
              </div>
              <div className="relative">
                <Slider
                  value={[data.culturePrefs.structured]}
                  onValueChange={(value) => 
                    onUpdate({ 
                      ...data, 
                      culturePrefs: { ...data.culturePrefs, structured: value[0] } 
                    })
                  }
                  min={0}
                  max={100}
                  step={10}
                  className="py-2"
                />
                <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 flex justify-center pointer-events-none">
                  <div className="w-px h-4 bg-border" />
                </div>
              </div>
            </div>

            {/* Traditional vs Innovative */}
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="font-medium text-foreground">Traditional</span>
                <span className="font-medium text-foreground">Innovative</span>
              </div>
              <div className="relative">
                <Slider
                  value={[data.culturePrefs.traditional]}
                  onValueChange={(value) => 
                    onUpdate({ 
                      ...data, 
                      culturePrefs: { ...data.culturePrefs, traditional: value[0] } 
                    })
                  }
                  min={0}
                  max={100}
                  step={10}
                  className="py-2"
                />
                <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 flex justify-center pointer-events-none">
                  <div className="w-px h-4 bg-border" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex gap-3">
        <Button variant="outline" className="flex-1" onClick={onBack}>
          Back
        </Button>
        <Button className="flex-1 gap-2" onClick={onNext}>
          Complete setup
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

export default OnboardingStep4;
